import { useCallback, useEffect, useState } from "react";
import { get as commsGet } from "../../../../service/comms/commsApi";
import { getCoachClients } from "../../../../service/comms/oversightService";
import { classifyOversightError } from "../../../../service/comms/oversightAnalyticsService";

/**
 * A coach's client roster (`GET /oversight/coaches/{coach_id}/clients`).
 *
 * Lifted out of the Clients tab because the Overview tab's cadence panel needs
 * the same roster — two components fetching it independently would double the
 * request and let the two panels disagree mid-flight.
 *
 * The endpoint returns the whole roster unpaginated, so callers may filter and
 * sort client-side without misreporting counts.
 *
 * @param {string|null|undefined} coachId - Resolved coach id.
 * @returns {{ clients: Array<Object>, loading: boolean,
 *   errorKind: 'forbidden'|'gateway'|'other'|null, retry: () => void }}
 */
export function useCoachClients(coachId) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  /** @type {[('forbidden'|'gateway'|'other'|null), Function]} */
  const [errorKind, setErrorKind] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Active-cleanup fetch, so a stale in-flight response can't clobber a newer one.
  useEffect(() => {
    if (!coachId) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setErrorKind(null);
    getCoachClients(commsGet, coachId)
      .then((data) => {
        if (!active) return;
        setClients(data?.clients || []);
      })
      .catch((err) => {
        if (!active) return;
        setErrorKind(classifyOversightError(err));
        setClients([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [coachId, reloadToken]);

  const retry = useCallback(() => setReloadToken((t) => t + 1), []);

  return { clients, loading, errorKind, retry };
}

export default useCoachClients;
